import React, { useEffect } from 'react';
import { Typography, Grid, Box } from '@material-ui/core';
import {
  SuggestedAddress,
  QuickAddressSearchParams,
  VerifyLevel,
} from '../../__generated__/pge-types';
import { QasResult } from '../../hooks/useQas';
import Modal from '../modal';
import EnteredArea from './EnteredArea';
import { useTranslation } from '../../hooks/useTranslation';
import Alerts from '../alerts';
import QasPicker from './QasPicker';
import Backdrop from '../backdrop';

type Props = {
  results: QasResult;
  onChange: (address: SuggestedAddress) => void;
  address: QuickAddressSearchParams;
  onUseEntered: () => void;
  onEdit: () => void;
  onSearch: (params: QuickAddressSearchParams, results: QasResult) => void;
  autoSelect?: boolean;
};

function useAlertText(results: QasResult): string | null {
  const { t } = useTranslation();

  if (results.type === 'NONE') {
    return t('NO_ADDRESS_MATCH');
  }

  if (results.type === 'SINGLE') {
    return t('ADDRESS_IS_INCOMPLETE');
  }

  if (results.type === 'MULTIPLE') {
    if (results.verifyLevel === VerifyLevel.Multiple) {
      return t('MULTIPLE_ADDRESSES');
    }

    if (results.verifyLevel === VerifyLevel.StreetPartial) {
      return t('STREET_PARTIAL_ADDRESS');
    }

    if (results.verifyLevel === VerifyLevel.PremisesPartial) {
      return t('PREMISE_PARTIAL_ADDRESS');
    }
  }

  return null;
}

export default function QasModal({
  address,
  results,
  onChange,
  onUseEntered,
  onEdit,
  onSearch,
  autoSelect = true,
}: Props) {
  const alertText = useAlertText(results);
  const { t } = useTranslation();

  useEffect(() => {
    if (autoSelect && results.type === 'AUTO') {
      onChange(results.address);
    }
  });

  return (
    <Modal
      onClose={onEdit}
      open={results.type !== 'AUTO' || !autoSelect}
      showCloseIcon
      title={<Typography variant="h2">{t('VERIFY_ADDRESS_TITLE')}</Typography>}
      className={'address-search-modal'}
    >
      <Box
        mt={2}
        maxHeight={400}
        style={{ overflowX: 'hidden', overflowY: 'auto' }}
      >
        <Grid container direction="column" spacing={2}>
          {alertText && (
            <Grid item>
              <Alerts
                disableColorOverride={true}
                isOpen
                severity="warning"
                message={alertText}
              />
            </Grid>
          )}
          <Grid item>
            <Grid container direction="row" spacing={8}>
              {results.type !== 'NONE' && (
                <Grid item xs={6}>
                  <QasPicker
                    onChange={onChange}
                    results={results}
                    onUseEntered={onUseEntered}
                    onEdit={onEdit}
                    onSearch={onSearch}
                    address={address}
                  />
                </Grid>
              )}

              <Grid item xs={results.type !== 'NONE' ? 6 : 12}>
                <EnteredArea
                  address={address}
                  onUseEntered={onUseEntered}
                  onEdit={onEdit}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <a
          href="#"
          onClick={(e: any) => {
            e.preventDefault();
            onEdit();
          }}
          style={{
            textDecoration: 'none',
            display: 'block',
            textAlign: 'right',
            paddingTop: 16,
          }}
        >
          {t('BACK')}
        </a>
      </Box>
      <Backdrop />
    </Modal>
  );
}
